import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { ReplaySubject } from 'rxjs';
import { env } from '../../environments/environment';
import { UserService } from '../user/user.service';
import { SegmentService } from 'ngx-segment-analytics';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authenticationState = new ReplaySubject<boolean>(1);
  private renewalScheduled = false;
  private renewalTimeoutId: any = null;
  private lastRenewalTime = 0;
  private renewInFlight = false;
  private renewBackoffMs = 5000; // start with 5s, exponential backoff on failures up to 60s

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private segment: SegmentService,
  ) {
    // Don't call checkToken() in constructor to avoid circular dependency
    // It will be called when needed by the application
  }

  /**
   * Check if user is authenticated by making a request to the server
   * The server will automatically include HttpOnly cookies in the request
   */
  async checkToken() {
    try {
      // Make a request to check authentication status
      // The browser will automatically include HttpOnly cookies
      const response = await this.http.get<any>(`${env.APIUrl}/auth/token`).toPromise();
      if (response?.user) {
        this.authenticationState.next(true);
        return true;
      } else {
        this.authenticationState.next(false);
        return false;
      }
    } catch (error) {
      // If the request fails (401), user is not authenticated
      this.authenticationState.next(false);
      return false;
    }
  }

  async impersonate(org: { id?: number }) {
    try {
      const response = await this.http.post<any>(`${env.APIUrl}/auth/impersonate-cookie`, org).toPromise();
      if (response?.success) {
        // The server should set the impersonated token as an HttpOnly cookie
        location.href = '/';
      }
      return false;
    } catch (e) {
      return e;
    }
  }

  async leaveImpersonation() {
    // Make a request to the server to clear the impersonated token cookie
    try {
      await this.http.post<any>(`${env.APIUrl}/auth/leave-impersonation-cookie`, {}).toPromise();
    } catch (e) {
      // Ignore errors
    }
    location.href = '/';
  }

  async renew() {
    try {
      if (this.renewInFlight) {
        return;
      }

      this.renewInFlight = true;
      const now = Date.now();
      // Prevent renewals that happen too frequently (less than 5 seconds apart)
      if (now - this.lastRenewalTime < 5000) {
        this.renewInFlight = false;
        return;
      }

      const response = await this.http.get<any>(`${env.APIUrl}/auth/renew-cookie`).toPromise();
      if (response?.success) {
        this.lastRenewalTime = now;
        // The server should set the new token as an HttpOnly cookie
        this.authenticationState.next(true);

        // Clear any existing renewal timeout
        if (this.renewalTimeoutId) {
          clearTimeout(this.renewalTimeoutId);
          this.renewalTimeoutId = null;
        }

        // Reset backoff after a successful renew
        this.renewBackoffMs = 5000;

        // Schedule next renewal if we have expiration info
        if (response.expiresIn && !this.renewalScheduled) {
          const expiresIn = response.expiresIn;
          const durationUntilExpiry = expiresIn - now - 10000; // 10 seconds before expiry

          if (durationUntilExpiry > 60000) {
            // Only schedule if more than 1 minute until expiry
            this.renewalScheduled = true;
            this.renewalTimeoutId = setTimeout(() => {
              this.renewalScheduled = false;
              this.renewalTimeoutId = null;
              this.renew();
            }, durationUntilExpiry);
          } else {
            this.renewalScheduled = false;
          }
        }
      }
    } catch (e) {
      const status = e?.status;
      // Only mark unauthenticated on explicit auth errors; on 5xx/network, keep state and retry with backoff
      if (status === 401 || status === 403) {
        this.authenticationState.next(false);
        this.renewalScheduled = false;
        if (this.renewalTimeoutId) {
          clearTimeout(this.renewalTimeoutId);
          this.renewalTimeoutId = null;
        }
      } else {
        // Schedule a backoff retry to avoid tight loops on 5xx/502 or network issues
        if (this.renewalTimeoutId) {
          clearTimeout(this.renewalTimeoutId);
          this.renewalTimeoutId = null;
        }
        const delay = this.renewBackoffMs;
        this.renewBackoffMs = Math.min(this.renewBackoffMs * 2, 60000);
        this.renewalScheduled = true;
        this.renewalTimeoutId = setTimeout(() => {
          this.renewalScheduled = false;
          this.renewalTimeoutId = null;
          this.renew();
        }, delay);
      }
    } finally {
      this.renewInFlight = false;
    }
  }

  async recover(info: { token: string; newPassword: string }) {
    return await this.http.post<any>(`${env.APIUrl}/auth/recover`, info).toPromise();
  }

  async forgot(email: string) {
    return await this.http.get<any>(`${env.APIUrl}/auth/recover`, { params: { email } }).toPromise();
  }

  async tokenInfo(token: string) {
    return await this.http.get<{ error?: string; email?: string }>(`${env.APIUrl}/auth/token/${token}`).toPromise();
  }

  async checkImpersonationStatus() {
    try {
      const response = await this.http.get<any>(`${env.APIUrl}/auth/impersonation-status`).toPromise();
      return response?.isImpersonated || false;
    } catch (e) {
      return false;
    }
  }

  async login(credentials: any) {
    try {
      const response = await this.http.post<any>(`${env.APIUrl}/auth/login-cookie`, credentials).toPromise();
      if (response?.success) {
        // The server should set the token as an HttpOnly cookie
        this.authenticationState.next(true);

        if (response.user?.type == 'AdminUser') {
          const profile = await this.userService.profile();

          this.segment.identify(profile.id, {
            email: profile.email,
            fullname: profile.name,
          });
          this.segment.group(profile.organization.id.toString(), { userId: profile.id });
        }

        // Schedule token renewal if we have expiration info
        if (response.expiresIn && !this.renewalScheduled) {
          const now = Date.now();
          const expiresIn = response.expiresIn;
          const durationUntilExpiry = expiresIn - now - 10000; // 10 seconds before expiry

          if (durationUntilExpiry > 60000) {
            // Only schedule if more than 1 minute until expiry
            // Add a small delay to ensure cookies are set before first renewal
            const initialDelay = Math.max(5000, durationUntilExpiry); // At least 5 seconds
            this.renewalScheduled = true;
            this.renewalTimeoutId = setTimeout(() => {
              this.renewalScheduled = false;
              this.renewalTimeoutId = null;
              this.renew();
            }, initialDelay);
          } else {
            //
          }
        }

        return true;
      }
      return false;
    } catch (e) {
      return e;
    }
  }

  async loginWithGoogle(code: string) {
    try {
      const response = await this.http
        .get<any>(`${env.APIUrl}/auth/google/code-cookie`, { params: { code } })
        .toPromise();
      if (response?.success) {
        // The server should set the token as an HttpOnly cookie
        this.authenticationState.next(true);

        if (response.user?.type == 'AdminUser') {
          const profile = await this.userService.profile();

          this.segment.identify(profile.id, {
            email: profile.email,
            fullname: profile.name,
          });
          this.segment.group(profile.organization.id.toString(), { userId: profile.id });
        }

        // Schedule token renewal if we have expiration info
        if (response.expiresIn && !this.renewalScheduled) {
          const now = Date.now();
          const expiresIn = response.expiresIn;
          const durationUntilExpiry = expiresIn - now - 10000; // 10 seconds before expiry

          if (durationUntilExpiry > 60000) {
            // Only schedule if more than 1 minute until expiry
            // Add a small delay to ensure cookies are set before first renewal
            const initialDelay = Math.max(5000, durationUntilExpiry); // At least 5 seconds
            this.renewalScheduled = true;
            this.renewalTimeoutId = setTimeout(() => {
              this.renewalScheduled = false;
              this.renewalTimeoutId = null;
              this.renew();
            }, initialDelay);
          } else {
            //
          }
        }

        return true;
      }
      return false;
    } catch (e) {
      return e;
    }
  }

  async logout() {
    // Revoke token in server and clear HttpOnly cookies
    try {
      // Try bearer-based logout first (will be ignored if no bearer is used)
      await this.http.post<any>(`${env.APIUrl}/auth/logout`, {}).toPromise();
    } catch (e) {
      // ignore
    }
    try {
      // Fallback to cookie-specific logout to ensure cookies are cleared
      await this.http.post<any>(`${env.APIUrl}/auth/logout-cookie`, {}).toPromise();
    } catch (e) {
      // Ignore errors, continue with client-side cleanup
    }

    // All auth cookies are HttpOnly and cleared server-side
    this.authenticationState.next(false);
    this.userService.cachedProfile = null;
    this.userService.clearCache();
    this.renewalScheduled = false; // Reset renewal flag
    if (this.renewalTimeoutId) {
      clearTimeout(this.renewalTimeoutId);
      this.renewalTimeoutId = null;
    }
    Sentry.setUser(null);
  }

  get isAuthenticated() {
    return this.authenticationState.asObservable();
  }

  checkPasswordStrength(password: string) {
    if (password.length < 10) {
      return false;
    }

    let typeCount = 0;

    // Has lowercase
    if (password.toUpperCase() !== password) {
      typeCount++;
    }

    // Has uppercase
    if (password.toLowerCase() !== password) {
      typeCount++;
    }

    // Has number
    if (/\d/.test(password)) {
      typeCount++;
    }

    // Has special characters (Any printable ASCII chars other than space, letters and digits)
    if (/[!-\/:-@[-`{-~]/.test(password)) {
      typeCount++;
    }

    return typeCount >= 3;
  }
}
